import * as RadPopover from '@radix-ui/react-popover'
import { motion } from 'motion/react'
import { useState } from 'react'
import styled, { css, keyframes } from 'styled-components'

import { Button } from '@blade/ui-kit'
import { CurrencyNetworkIcon } from '@root/entities/currency/ui'
import { Network } from '@root/entities/network'
import { WalletAddressTruncated } from '@root/entities/wallet/ui/common/wallet-address-truncated'
import { StringUtil } from '@root/shared'
import { useTranslation } from '@root/shared/localization'
import {
  ArrowDown2Icon,
  ArrowUp2Icon,
  mediaMaxMobile,
  WalletOutlineIcon,
} from '@root/shared/ui-kit'

import { ALL_WALLETS } from '../../atoms'

import { WalletSelectorItem } from './wallet-selector-item'

import type { ProfileWallet } from '@root/entities/profile/model/profile-wallet'

export const WalletSelector = (props: {
  wallets: ReadonlyArray<ProfileWallet>
  onSelectWallet: (wallet: ProfileWallet | typeof ALL_WALLETS) => void
  selectedWallet: ProfileWallet | undefined
}) => {
  const { selectedWallet } = props
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)
  const Cmp = open ? WalletIconUp : WalletIconDown
  const handleSelectWallet = (wallet: ProfileWallet | typeof ALL_WALLETS) => {
    setOpen(false)
    props.onSelectWallet(wallet)
  }

  return (
    <Root
      open={open}
      onOpenChange={props.wallets.length > 0 ? setOpen : undefined}
    >
      <Trigger asChild>
        <WalletWrapper
          type="button"
          layout
          transition={{ duration: 0.15, ease: 'easeInOut' }}
        >
          <WalletIconWrapper>
            <WalletOutlineIcon size={22} />
          </WalletIconWrapper>
          <WalletAddress_>
            {selectedWallet ? (
              <WalletAddressTruncated walletAddress={selectedWallet.address} />
            ) : (
              StringUtil.capitalize(t('wallet.all_wallets'))
            )}
          </WalletAddress_>

          {props.wallets.length > 0 ? <Cmp size={16} /> : undefined}
        </WalletWrapper>
      </Trigger>
      {selectedWallet ? (
        <NetworkBadgeWrapper>
          <NetworkBadge>
            <CurrencyNetworkIcon network={selectedWallet.network} size={16} />
            {Network.toDisplayString(selectedWallet.network).toUpperCase()}
          </NetworkBadge>
        </NetworkBadgeWrapper>
      ) : undefined}
      <RadPopover.Portal>
        <Content>
          <WalletList>
            {props.wallets.map((wallet) => (
              <WalletSelectorItem
                key={wallet.address}
                wallet={wallet}
                active={wallet.address === selectedWallet?.address}
                onClick={() => handleSelectWallet(wallet)}
                size="s"
              />
            ))}
          </WalletList>
          <Button
            color="primary"
            variant="solid"
            radius="rounded"
            size="h28"
            onClick={() => handleSelectWallet(ALL_WALLETS)}
          >
            {t('wallet.all_wallets')}
          </Button>
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const Root = styled(RadPopover.Root)`
  display: grid;
  grid-template-columns: auto auto;

  height: 36px;
  justify-items: center;
  align-items: center;
`
const WalletWrapper = styled(motion.button)`
  all: unset;

  color: var(--text-11);

  box-sizing: border-box;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  height: 100%;
  gap: 4px;
  background: var(--backgroundA-3);

  cursor: pointer;
  border-radius: 8px;
  padding-inline: 6px;

  transition: all 0.15s ease-in-out;

  > svg {
    transition: all 0.15s ease-in-out;
    color: var(--text-11);
  }

  &:hover {
    background: var(--backgroundA-4);

    color: var(--text-12);

    > svg {
      color: var(--text-12);
    }
  }
`
const WalletIconWrapper = styled.div`
  ${mediaMaxMobile(css`
    display: none;
  `)}
`

const NetworkBadgeWrapper = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-11);
  display: flex;
  align-items: center;
  justify-content: center;

  height: 100%;
`
const NetworkBadge = styled.div`
  display: flex;
  padding: 5px;
  gap: 5px;
  border-radius: 8px;
  background: var(--backgroundA-2);

  ${mediaMaxMobile(css`
    background: transparent;
  `)}
`
const WalletAddress_ = styled.div`
  line-height: normal;
`
const WalletIconUp = styled(ArrowUp2Icon)`
  color: var(--text-12);
`

const WalletIconDown = styled(ArrowDown2Icon)`
  color: var(--text-12);
`

const Trigger = styled(RadPopover.Trigger)``

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Content = styled(RadPopover.Content)`
  border: 0.5px solid var(--background-3);
  background: var(--background-2);
  box-shadow: 0 12px 52px 0 rgba(0, 0, 0, 0.87);

  border-radius: 8px;
  margin-top: 4px;
  padding: 6px;

  animation-duration: 200ms;
  animation-name: ${contentShow};

  display: grid;
  gap: 4px;
`

const WalletList = styled.div`
  display: grid;
  gap: 4px;
  min-width: var(--radix-popover-trigger-width);
`
